import {
  Cartesian3,
  Matrix3,
  Matrix4,
  Quaternion,
  HeadingPitchRoll,
  Transforms,
  Math as CesiumMath,
} from "@cesium/engine";

/**
 * position을 기준으로 translation, rotation, scale이 적용된 4x4 matrix 계산
 * @param {Cartesian3} position 중심점
 * @param {Cartesian3} translation 이동량
 * @param {Quaternion | HeadingPitchRoll} rotation 회전량
 * @param {Cartesian3} scale 스케일
 * @returns {Matrix4} matrix 4x4 행렬
 */
const computeMatrix = (position, translation, rotation, scale) => {
  // HeadingPitchRoll  => Quaternion
  if (rotation instanceof HeadingPitchRoll) {
    rotation = Quaternion.fromHeadingPitchRoll(rotation, new Quaternion());
  }

  //# ENU -> ECEF convert value
  const toGlobal = Transforms.eastNorthUpToFixedFrame(position);
  const inverse = Matrix4.inverse(toGlobal, new Matrix4());

  //# combine matrix => scale x rotation
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    Cartesian3.ZERO,
    rotation,
    scale,
    new Matrix4()
  );

  //# compute transform
  const transform = Matrix4.multiply(
    toGlobal,
    Matrix4.multiply(matrix, inverse, new Matrix4()),
    new Matrix4()
  );

  //# set translation
  Matrix4.setTranslation(
    transform,
    new Cartesian3(
      transform[12] + translation.x,
      transform[13] + translation.y,
      transform[14] + translation.z
    ),
    transform
  );

  /**
   * CreateRegion 버그 임시 방편 코드
   * https://github.com/SOYIJUN/ditapjs/issues/187
   */
  transform[14] += CesiumMath.EPSILON7;

  return transform;
};

/**
 * Cesium3DTileset Editing Class
 * @alias Edit3DTileset
 * @private
 */
class Edit3DTileset {
  /**
   * BoundingSphere 추출
   * @param {Cesium3DTileset} object
   * @returns {BoundingSphere}
   */
  static getBoundingSphere(object) {
    const boundingSphere = object.boundingSphere;

    return boundingSphere;
  }

  /**
   * Transform 정보 추출
   * @param {Cesium3DTileset} object
   * @param {boolean} enu
   * @returns {{
   *  position: Cartesian3,
   *  originalPosition: Cartesian3,
   *  translation: Cartesian3,
   *  quaternion: Quaternion,
   *  HeadingPitchRoll: HeadingPitchRoll,
   *  scale: Cartesian3
   * }}
   */
  static decomposition(object, enu = true) {
    const matrix4 = object.modelMatrix; // 원본 행렬 B

    const position = object.boundingSphere.center; // 현재 중심점
    const originalPosition = Matrix4.multiplyByPoint(
      // 원본 중심점
      Matrix4.inverse(matrix4, new Matrix4()),
      position,
      new Cartesian3()
    );
    const translation = Cartesian3.subtract(
      position,
      originalPosition,
      new Cartesian3()
    );

    let scale;
    let quaternion;
    let headingPitchRoll;
    if (enu) {
      const originalEnu = Transforms.eastNorthUpToFixedFrame(originalPosition); // 원본 중심정의 EastNorthUp 행렬 A
      const originalEnuInverse = Matrix4.inverse(originalEnu, new Matrix4()); // Inverse A^{-1}

      // A^{-1}BA => ENU 행렬 제거한 행렬 추출
      const matrix4_2 = Matrix4.multiply(
        originalEnuInverse,
        matrix4,
        new Matrix4()
      );
      const matrix4_1 = Matrix4.multiply(matrix4_2, originalEnu, new Matrix4());

      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4_1, new Matrix3());

      scale = Matrix4.getScale(matrix4_1, new Cartesian3());
      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    } else {
      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4, new Matrix3());

      scale = Matrix4.getScale(matrix4, new Cartesian3());
      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    }

    const result = {
      position: position,
      originalPosition: originalPosition,
      translation: translation,
      quaternion: quaternion,
      headingPitchRoll: headingPitchRoll,
      scale: scale,
    };

    return result;
  }

  /**
   * Transform 초기화
   * @param {Cesium3DTileset}
   * @returns {Cesium3DTileset | undefined}
   */
  static reset(object) {
    object.modelMatrix = Matrix4.IDENTITY;

    return object;
  }

  /**
   * Matrix4 적용
   * @param {Cesium3DTileset} object
   * @param {Matrix4} matrix
   * @returns {Matrix4 | undefined}
   */
  static setMatrix(object, matrix) {
    object.modelMatrix = matrix;
  }

  /**
   * Matirx 추출
   * @param {Cesium3DTileset} object
   * @returns {Matrix4}
   */
  static getMatrix(object) {
    return object.modelMatrix;
  }

  /**
   * 좌표 설정
   * @param {Cesium3DTileset} object
   * @param {Cartesian3} position
   * @returns {Cartesian3}
   */
  static setPosition(object, position) {
    const { originalPosition, scale, quaternion } = this.decomposition(object);

    const translation = Cartesian3.subtract(
      position,
      originalPosition,
      new Cartesian3()
    );

    const matrix = computeMatrix(
      originalPosition,
      translation,
      quaternion,
      scale
    );

    object.modelMatrix = matrix;

    return position;
  }

  /**
   * 좌표 추출
   * @param {Cesium3DTileset} object
   * @returns {Cartesian3}
   */
  static getPosition(object) {
    const { position } = this.decomposition(object);

    return position;
  }

  /**
   * 회전 설정
   * @param {Cesium3DTileset} object
   * @param {Quaternion | HeadingPitchRoll} rotation
   * @returns {Quaternion | HeadingPitchRoll}
   */
  static setRotation(object, rotation) {
    const { originalPosition, translation, scale } = this.decomposition(object);

    const matrix = computeMatrix(
      originalPosition,
      translation,
      rotation,
      scale
    );

    object.modelMatrix = matrix;

    return rotation;
  }

  /**
   * Quaternion 추출
   * @param {Cesium3DTileset} object
   * @param {boolean} enu
   * @returns {Quaternion}
   */
  static getQuaternion(object, enu = true) {
    const { quaternion } = this.decomposition(object, enu);

    return quaternion;
  }

  /**
   * HeadingPitchRoll 추출
   * @param {Cesium3DTileset} object
   * @param {boolean} enu
   * @returns {HeadingPitchRoll}
   */
  static getHeadingPitchRoll(object, enu = true) {
    const { headingPitchRoll } = this.decomposition(object, enu);

    return headingPitchRoll;
  }

  /**
   * Scale 설정
   * @param {Cesium3DTileset} object
   * @param {Cartesian3} scale
   * @returns {Cartesian3}
   */
  static setScale(object, scale) {
    const { originalPosition, translation, quaternion } = this.decomposition(
      object
    );

    const matrix = computeMatrix(
      originalPosition,
      translation,
      quaternion,
      scale
    );

    object.modelMatrix = matrix;

    return scale;
  }

  /**
   * Scale 추출
   * @param {Cesium3DTileset} object
   * @returns {Cartesian3}
   */
  static getScale(object) {
    const { scale } = this.decomposition(object);

    return scale;
  }

  /**
   * TranslationRotationScale 적용
   * @param {Cesium3DTileset} object
   * @param {Cartesian3} translation
   * @param {Quaternion | HeadingPitchRoll} rotation
   * @param {Cartesian3} scale
   * @returns {{
   * position: Cartesian3,
   * translation: Cartesian3,
   * rotation: Quaternion | HeadingPitchRoll,
   * scale: Cartesian3
   * }}
   */
  static setTranslationRotationScale(object, translation, rotation, scale) {
    // 원본 좌표
    const { originalPosition } = this.decomposition(object);

    // 원본 좌표 + translation
    const position = Cartesian3.add(
      originalPosition,
      translation,
      new Cartesian3()
    );

    // 메트릭스 계산
    const matrix = computeMatrix(
      originalPosition,
      translation,
      rotation,
      scale
    );

    // set matrix
    object.modelMatrix = matrix;

    return {
      position: position,
      translation: translation,
      rotation: rotation,
      scale: scale,
    };
  }
}

export default Edit3DTileset;
